import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatYen } from "@/lib/money";

import type { TenderPreview } from "@/lib/types";

import type { ReceiptChoice } from "./useTender";

/**
 * 領収書 (design/05 §3.2, §5): 適格簡易請求書 (default) or 適格請求書 with the buyer's name; give it
 * printed / by e-mail / as a QR on the screen. 印紙税: a paper receipt for cash ≥ ¥50,000 needs a
 * ¥200 stamp — the server says so (`stampTaxWarning`) and one tap switches to a digital receipt;
 * keeping paper, the cashier ticks "the stamp goes on" (T13-24 — counted on the Z-report).
 */
export function ReceiptOptions({
  value,
  onChange,
  preview,
}: {
  value: ReceiptChoice;
  onChange: (next: ReceiptChoice) => void;
  preview: TenderPreview | undefined;
}) {
  const { t, i18n } = useTranslation();
  const set = (changes: Partial<ReceiptChoice>) => onChange({ ...value, ...changes });
  return (
    <div className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 font-medium">{t("tender.receipt_title")}</legend>
        {(["kani_invoice", "tekikaku_invoice"] as const).map((kind) => (
          <label key={kind} className="flex min-h-12 items-center gap-3 rounded-md border px-3">
            <input
              type="radio"
              name="document"
              checked={value.documentType === kind}
              onChange={() => set({ documentType: kind })}
              className="size-5"
            />
            {t(kind === "kani_invoice" ? "tender.receipt_kani" : "tender.receipt_tekikaku")}
          </label>
        ))}
        {value.documentType === "tekikaku_invoice" ? (
          <input
            value={value.recipient}
            onChange={(e) => set({ recipient: e.target.value })}
            placeholder={t("tender.recipient")}
            aria-label={t("tender.recipient")}
            maxLength={120}
            className="min-h-12 rounded-md border px-3 text-lg"
          />
        ) : null}
      </fieldset>
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 font-medium">{t("tender.issue_title")}</legend>
        <label className="flex min-h-12 items-center gap-3 rounded-md border px-3">
          <input
            type="checkbox"
            checked={value.print}
            onChange={(e) => set({ print: e.target.checked })}
            className="size-5"
          />
          {t("tender.issue_print")}
        </label>
        <label className="flex min-h-12 items-center gap-3 rounded-md border px-3">
          <input
            type="checkbox"
            checked={value.email}
            onChange={(e) => set({ email: e.target.checked })}
            className="size-5"
          />
          {t("tender.issue_email")}
        </label>
        {value.email ? (
          <input
            type="email"
            value={value.emailTo}
            onChange={(e) => set({ emailTo: e.target.value })}
            placeholder={t("tender.email_placeholder")}
            aria-label={t("tender.email_placeholder")}
            className="min-h-12 rounded-md border px-3 text-lg"
          />
        ) : null}
        <label className="flex min-h-12 items-center gap-3 rounded-md border px-3">
          <input
            type="checkbox"
            checked={value.qr}
            onChange={(e) => set({ qr: e.target.checked })}
            className="size-5"
          />
          {t("tender.issue_qr")}
        </label>
      </fieldset>
      {preview?.stampTaxWarning ? (
        <div
          role="alert"
          data-testid="stamp-warning"
          className="flex flex-col gap-2 rounded-md border border-status-pending bg-status-pending/20 p-3"
        >
          <p className="flex items-start gap-2">
            <TriangleAlert aria-hidden className="mt-0.5 size-5 shrink-0" />
            {t("tender.stamp_warning", {
              threshold: formatYen(
                preview.stampTax?.threshold ?? 50000,
                i18n.resolvedLanguage ?? "ja",
              ),
            })}
          </p>
          {value.print ? (
            <label className="flex min-h-12 items-center gap-3 rounded-md border bg-background px-3">
              <input
                type="checkbox"
                data-testid="stamp-affixed"
                checked={value.stampAffixed}
                onChange={(e) => set({ stampAffixed: e.target.checked })}
                className="size-5"
              />
              {t("tender.stamp_affixed")}
            </label>
          ) : null}
          <button
            type="button"
            onClick={() => set({ print: false, qr: true, stampAffixed: false })}
            className="min-h-12 self-start rounded-md border bg-background px-4 font-semibold"
          >
            {t("tender.switch_electronic")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
